import React, { useState, useMemo } from 'react';
import { FileText, Copy, Check, Trash2, Target } from 'lucide-react';

const STOP_WORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
  'of', 'with', 'by', 'from', 'is', 'it', 'as', 'was', 'are', 'be',
  'this', 'that', 'have', 'has', 'had', 'not', 'they', 'we', 'you', 'i',
  'he', 'she', 'do', 'does', 'did', 'will', 'would', 'could', 'should',
  'may', 'can', 'if', 'so', 'no', 'up', 'my', 'his', 'her', 'its',
  'am', 'been', 'were', 'being', 'their', 'them', 'than', 'then', 'what',
  'which', 'who', 'when', 'where', 'how', 'all', 'each', 'every', 'both',
  'more', 'some', 'any', 'most', 'other', 'into', 'over', 'after', 'about',
]);

const WordCounter: React.FC = () => {
  const [text, setText] = useState('');
  const [copied, setCopied] = useState(false);
  const [wordGoal, setWordGoal] = useState<number>(0);
  const [goalInput, setGoalInput] = useState('');

  const stats = useMemo(() => {
    const chars = text.length;
    const charsNoSpaces = text.replace(/\s/g, '').length;
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    const sentences = text.trim() ? (text.match(/[.!?]+/g) || []).length || (text.trim() ? 1 : 0) : 0;
    const paragraphs = text.trim() ? text.split(/\n\s*\n/).filter(p => p.trim()).length : 0;
    const lines = text ? text.split('\n').length : 0;
    const avgWordLen = words > 0 ? (charsNoSpaces / words).toFixed(1) : '0';
    const readingTime = Math.max(1, Math.ceil(words / 200));
    const speakingTime = Math.max(1, Math.ceil(words / 130));

    return { chars, charsNoSpaces, words, sentences, paragraphs, lines, avgWordLen, readingTime, speakingTime };
  }, [text]);

  const topWords = useMemo(() => {
    if (!text.trim()) return [];
    const wordList = text.toLowerCase().match(/[a-z']+/g) || [];
    const freq = new Map<string, number>();
    for (const w of wordList) {
      if (w.length < 2 || STOP_WORDS.has(w)) continue;
      freq.set(w, (freq.get(w) || 0) + 1);
    }
    return [...freq.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [text]);

  const charDist = useMemo(() => {
    if (!text) return { letters: 0, numbers: 0, spaces: 0, punctuation: 0 };
    let letters = 0, numbers = 0, spaces = 0, punctuation = 0;
    for (const ch of text) {
      if (/[a-zA-Z]/.test(ch)) letters++;
      else if (/[0-9]/.test(ch)) numbers++;
      else if (/\s/.test(ch)) spaces++;
      else punctuation++;
    }
    const total = text.length || 1;
    return {
      letters: Math.round((letters / total) * 100),
      numbers: Math.round((numbers / total) * 100),
      spaces: Math.round((spaces / total) * 100),
      punctuation: Math.round((punctuation / total) * 100),
    };
  }, [text]);

  const copyText = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const toTitleCase = (t: string) => t.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

  const setGoal = () => {
    const val = parseInt(goalInput);
    if (val > 0) setWordGoal(val);
  };

  const goalProgress = wordGoal > 0 ? Math.min(100, (stats.words / wordGoal) * 100) : 0;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-100 rounded-lg">
          <FileText className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Word Counter</h1>
          <p className="text-gray-500 text-sm">Count words, characters, and analyze your text</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: textarea */}
        <div className="lg:col-span-2 space-y-4">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type or paste your text here..."
            className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-y focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800"
          />

          {/* Text tool buttons */}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setText(text.toUpperCase())} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm">UPPERCASE</button>
            <button onClick={() => setText(text.toLowerCase())} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm">lowercase</button>
            <button onClick={() => setText(toTitleCase(text))} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm">Title Case</button>
            <button onClick={copyText} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm flex items-center gap-1">
              {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />} Copy
            </button>
            <button onClick={() => setText('')} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Clear
            </button>
          </div>

          {/* Top words */}
          {topWords.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Top Words</h3>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {topWords.map(([word, count]) => (
                  <div key={word} className="flex justify-between items-center bg-white rounded-md px-3 py-1.5 text-sm border">
                    <span className="text-gray-800 font-medium truncate">{word}</span>
                    <span className="text-gray-400 ml-2">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Character distribution */}
          {text.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Character Distribution</h3>
              <div className="space-y-2">
                {[
                  { label: 'Letters', value: charDist.letters, color: 'bg-blue-500' },
                  { label: 'Numbers', value: charDist.numbers, color: 'bg-green-500' },
                  { label: 'Spaces', value: charDist.spaces, color: 'bg-yellow-500' },
                  { label: 'Punctuation', value: charDist.punctuation, color: 'bg-red-500' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="flex items-center gap-3 text-sm">
                    <span className="text-gray-600 w-24">{label}</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2.5">
                      <div className={`${color} h-2.5 rounded-full transition-all`} style={{ width: `${value}%` }} />
                    </div>
                    <span className="text-gray-500 w-10 text-right">{value}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: stats panel */}
        <div className="space-y-4">
          {/* Main stats */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">Statistics</h3>
            {[
              { label: 'Words', value: stats.words },
              { label: 'Characters', value: stats.chars },
              { label: 'Characters (no spaces)', value: stats.charsNoSpaces },
              { label: 'Sentences', value: stats.sentences },
              { label: 'Paragraphs', value: stats.paragraphs },
              { label: 'Lines', value: stats.lines },
              { label: 'Avg. Word Length', value: stats.avgWordLen },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between text-sm">
                <span className="text-gray-500">{label}</span>
                <span className="font-semibold text-gray-800">{value}</span>
              </div>
            ))}
          </div>

          {/* Reading / speaking time */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h3 className="text-sm font-semibold text-gray-700">Time Estimates</h3>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Reading time</span>
              <span className="font-semibold text-gray-800">{stats.words === 0 ? '0' : stats.readingTime} min</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Speaking time</span>
              <span className="font-semibold text-gray-800">{stats.words === 0 ? '0' : stats.speakingTime} min</span>
            </div>
          </div>

          {/* Word goal */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1">
              <Target className="w-4 h-4" /> Word Goal
            </h3>
            {wordGoal === 0 ? (
              <div className="flex gap-2">
                <input
                  type="number"
                  value={goalInput}
                  onChange={(e) => setGoalInput(e.target.value)}
                  placeholder="e.g. 500"
                  className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                />
                <button onClick={setGoal} className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">Set</button>
              </div>
            ) : (
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">{stats.words} / {wordGoal} words</span>
                  <span className={`font-semibold ${goalProgress >= 100 ? 'text-green-600' : 'text-blue-600'}`}>
                    {Math.round(goalProgress)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${goalProgress >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                    style={{ width: `${Math.min(100, goalProgress)}%` }}
                  />
                </div>
                <button
                  onClick={() => { setWordGoal(0); setGoalInput(''); }}
                  className="text-xs text-gray-400 hover:text-gray-600 mt-2"
                >
                  Clear goal
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WordCounter;
